import React from 'react';

import { copyReadyIn, type LocalizedCopy } from '../ad-language';
import { localizeAdCopy } from '../ad-language-api';
import type { AdSubject, AdSubjectType } from '../banner-kit-api';

export interface UseLocalizedCopyArgs {
  tenantId: number | undefined;
  /** The asset's language; '' or undefined = none (an older document: nothing to localize). */
  language: string | undefined;
  subjectType: AdSubjectType;
  /** The advertised subject as the asset snapshotted it; null while none is picked. */
  subject: AdSubject | null | undefined;
  /** The copy the asset already holds (its `localized_copy`): when it is this subject's in this language, nothing
   *  is read. */
  current: LocalizedCopy | null | undefined;
}

export interface LocalizedCopyState {
  /** `idle`: no language, subject or tenant. `ready`: `copy` is the subject's copy in the language. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  copy: LocalizedCopy | null;
  /** Reads the copy again after an error. */
  retry: () => void;
}

/**
 * The advertised subject's copy in the asset's language, for the composer to write into its document as
 * `localized_copy` (an automatic fill: never through a path that forks a draft holding art). With the matching copy
 * already held (`current`), that copy. A game or feature with no subtitle or CTA has nothing to translate: its copy
 * is built here (headline = its headline, else its name; an empty CTA so the per-language default applies). Otherwise
 * `ad-copy-localize` is read; a change of language or subject aborts the read in flight and a late answer for an older
 * pick is dropped.
 */
export const useLocalizedCopy = ({
  tenantId,
  language,
  subjectType,
  subject,
  current,
}: UseLocalizedCopyArgs): LocalizedCopyState => {
  const lang = (language ?? '').trim();
  const id = subject?.id ?? '';
  const name = subject?.name ?? '';
  const headline = subject?.headline ?? '';
  const subtitle = subject?.subtitle ?? '';
  const cta = subject?.cta_text ?? '';
  const active = Boolean(tenantId && lang && subject);
  const held = active && copyReadyIn(current, lang, id);
  const needsRead = subjectType === 'promotion' || subtitle.trim() !== '' || cta.trim() !== '';
  const wantRead = active && !held && needsRead;

  const [attempt, setAttempt] = React.useState(0);
  const [answer, setAnswer] = React.useState<{ key: string; status: 'ready' | 'error'; copy: LocalizedCopy | null }>();
  // What one read is for: an answer counts only while it still matches.
  const key = JSON.stringify([tenantId, lang, subjectType, id, name, headline, subtitle, cta, attempt]);

  // Syncs with the network: reads the subject's copy in the language (aborted when the pick changes).
  React.useEffect(() => {
    if (!wantRead || !tenantId) return undefined;
    const controller = new AbortController();
    let live = true;
    void localizeAdCopy(
      tenantId,
      { language: lang, subjectType, subject: { id, name, headline, subtitle, cta_text: cta } },
      controller.signal,
    ).then((res) => {
      if (!live) return;
      if (res.ok) setAnswer({ key, status: 'ready', copy: res.data });
      else if (!res.aborted) setAnswer({ key, status: 'error', copy: null });
    });
    return () => {
      live = false;
      controller.abort();
    };
  }, [wantRead, key, tenantId, lang, subjectType, id, name, headline, subtitle, cta]);

  const retry = React.useCallback((): void => setAttempt((n) => n + 1), []);

  const local = React.useMemo<LocalizedCopy | null>(
    () =>
      active && !needsRead
        ? { language: lang, subject_id: id, headline: headline || name, subtitle: '', cta_text: '', translated: [] }
        : null,
    [active, needsRead, lang, id, headline, name],
  );

  const view: Pick<LocalizedCopyState, 'status' | 'copy'> = !active
    ? { status: 'idle', copy: null }
    : held && current
      ? { status: 'ready', copy: current }
      : local
        ? { status: 'ready', copy: local }
        : answer?.key === key
          ? { status: answer.status, copy: answer.copy }
          : { status: 'loading', copy: null };

  return React.useMemo(() => ({ status: view.status, copy: view.copy, retry }), [view.status, view.copy, retry]);
};

export default useLocalizedCopy;
